import {
  canAccessCompanySettings,
  companySettingsTabFromPathname,
  isCompanySettingsTabVisible,
} from '../pages/company-settings/company-settings-access.util';
import { PageRoute } from '../types/page.types';
import { AccessRole, StakeholderRole } from '../types/stakeholder.types';

export type PageAccessCompany = {
  isDemo?: boolean;
  roleInCompany: StakeholderRole;
  stakeholder?: {
    accessRole?: AccessRole | null;
    capTableAccess?: boolean;
  };
};

const MANAGERS: StakeholderRole[] = [StakeholderRole.ADMIN, StakeholderRole.CONTRIBUTOR];
const ALL_MEMBERS: StakeholderRole[] = [
  StakeholderRole.ADMIN,
  StakeholderRole.CONTRIBUTOR,
  StakeholderRole.STAKEHOLDER,
];

const DOCUMENT_PATH_PREFIXES: PageRoute[] = [
  PageRoute.DOCUMENTS,
  PageRoute.DOCUMENTS_TEMPLATES,
  PageRoute.DOCUMENTS_SIGNATURES,
];

type ModuleAccessPolicy = {
  allowedPaths?: (company: PageAccessCompany) => readonly string[];
  canAccess?: (company: PageAccessCompany, pathname: string) => boolean;
  deniedRedirect?: string;
  inRoleSidebar?: boolean;
  module: PageRoute;
  pathPrefixes?: readonly string[];
  roles: readonly StakeholderRole[];
};

const pathMatches = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

export const getRoleHomeRoute = (role: StakeholderRole): PageRoute =>
  role === StakeholderRole.STAKEHOLDER ? PageRoute.MY_HOLDINGS : PageRoute.DASHBOARD;

export const getDocumentSubMenuPaths = (role?: StakeholderRole): PageRoute[] =>
  role === StakeholderRole.STAKEHOLDER
    ? [PageRoute.DOCUMENTS_SIGNATURES]
    : [PageRoute.DOCUMENTS_TEMPLATES, PageRoute.DOCUMENTS_SIGNATURES];

const canAccessCompanySettingsPath = (company: PageAccessCompany, pathname: string): boolean => {
  if (company.isDemo) return false;
  if (!canAccessCompanySettings(company)) return false;
  const tab = companySettingsTabFromPathname(pathname);
  if (pathname !== PageRoute.COMPANY_SETTINGS && tab === null) return false;
  return !tab || isCompanySettingsTabVisible(company, tab);
};

const MODULE_ACCESS: ModuleAccessPolicy[] = [
  { module: PageRoute.DASHBOARD, roles: MANAGERS },
  { module: PageRoute.STAKEHOLDERS, roles: MANAGERS },
  { module: PageRoute.SHARE_CLASSES, roles: MANAGERS },
  { module: PageRoute.POOL_PLANS, roles: MANAGERS },
  { module: PageRoute.EVENTS, roles: MANAGERS },
  { module: PageRoute.SCENARIO_MODELING, roles: MANAGERS },
  {
    module: PageRoute.CAP_TABLE,
    roles: ALL_MEMBERS,
    canAccess: (company) => Boolean(company.stakeholder?.capTableAccess),
  },
  { module: PageRoute.SAFES, roles: MANAGERS },
  { module: PageRoute.MY_HOLDINGS, roles: ALL_MEMBERS },
  { module: PageRoute.DIVIDENDS, roles: MANAGERS },
  {
    module: PageRoute.DOCUMENTS,
    roles: ALL_MEMBERS,
    pathPrefixes: DOCUMENT_PATH_PREFIXES,
    canAccess: (company) => !company.isDemo,
    allowedPaths: (company) =>
      company.roleInCompany === StakeholderRole.STAKEHOLDER
        ? getDocumentSubMenuPaths(company.roleInCompany)
        : DOCUMENT_PATH_PREFIXES,
    deniedRedirect: PageRoute.DOCUMENTS_SIGNATURES,
  },
  { module: PageRoute.VAULT, roles: ALL_MEMBERS },
  {
    module: PageRoute.COMPANY_SETTINGS,
    roles: MANAGERS,
    inRoleSidebar: false,
    canAccess: canAccessCompanySettingsPath,
  },
];

const STAKEHOLDER_SIDEBAR_ORDER: readonly PageRoute[] = [
  PageRoute.MY_HOLDINGS,
  PageRoute.DOCUMENTS,
  PageRoute.CAP_TABLE,
  PageRoute.VAULT,
];

const sidebarRank = (link: PageRoute, order: readonly PageRoute[]): number => {
  const rank = order.indexOf(link);
  return rank === -1 ? order.length : rank;
};

const sidebarModulesFor = (role: StakeholderRole): PageRoute[] => {
  const modules = MODULE_ACCESS.filter(
    (policy) => policy.inRoleSidebar !== false && policy.roles.includes(role),
  ).map((policy) => policy.module);

  if (role !== StakeholderRole.STAKEHOLDER) {
    return modules;
  }

  return [...modules].sort(
    (left, right) =>
      sidebarRank(left, STAKEHOLDER_SIDEBAR_ORDER) - sidebarRank(right, STAKEHOLDER_SIDEBAR_ORDER),
  );
};

export const roleModuleLinks: Record<StakeholderRole, readonly PageRoute[]> = {
  [StakeholderRole.ADMIN]: sidebarModulesFor(StakeholderRole.ADMIN),
  [StakeholderRole.CONTRIBUTOR]: sidebarModulesFor(StakeholderRole.CONTRIBUTOR),
  [StakeholderRole.STAKEHOLDER]: sidebarModulesFor(StakeholderRole.STAKEHOLDER),
};

const policyPathPrefixes = (policy: ModuleAccessPolicy): readonly string[] =>
  policy.pathPrefixes ?? [policy.module];

const findPolicy = (pathname: string): ModuleAccessPolicy | undefined =>
  MODULE_ACCESS.find((policy) =>
    policyPathPrefixes(policy).some((prefix) => pathMatches(pathname, prefix)),
  );

const isModuleAllowed = (
  policy: ModuleAccessPolicy,
  company: PageAccessCompany,
  pathname: string,
): boolean =>
  policy.roles.includes(company.roleInCompany) &&
  (!policy.canAccess || policy.canAccess(company, pathname));

export const isModuleLinkVisible = (link: PageRoute, company: PageAccessCompany): boolean => {
  const policy = MODULE_ACCESS.find((item) => item.module === link);
  return Boolean(policy && isModuleAllowed(policy, company, link));
};

export const getUnauthorizedRedirect = (
  pathname: string,
  company: PageAccessCompany,
): null | string => {
  const policy = findPolicy(pathname);
  if (!policy) return null;
  if (!isModuleAllowed(policy, company, pathname)) {
    return getRoleHomeRoute(company.roleInCompany);
  }
  if (
    policy.allowedPaths &&
    !policy.allowedPaths(company).some((prefix) => pathMatches(pathname, prefix))
  ) {
    return policy.deniedRedirect ?? getRoleHomeRoute(company.roleInCompany);
  }
  return null;
};
